'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { Sparkles, X, CornerDownLeft, ArrowRight, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useFocusTrap } from '@/lib/use-focus-trap'

type Source = { slug: string; title: string; url: string; category: string }
type AskState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'answered'; answer: string | null; message?: string; sources: Source[]; enabled: boolean }
  | { status: 'error'; message: string }

// "Ask AI" docs island: a button that opens a modal where a reader can
// ask a question in natural language. The question is POSTed to
// /api/help/ask, which retrieves the most relevant guides and (when
// ANTHROPIC_API_KEY is set server-side) returns a grounded answer with
// citations. When AI is disabled the endpoint returns the matching
// guides instead, and this UI shows them as a normal result list — so
// the feature degrades cleanly with no client-side env knowledge.
export function HelpAskAi({
  variant = 'button',
  open: controlledOpen,
  onOpenChange,
  initialQuery,
  hideTrigger = false,
}: {
  variant?: 'button' | 'inline'
  open?: boolean
  onOpenChange?: (open: boolean) => void
  // Pre-fill the question (e.g. handed over from Cmd-K search).
  initialQuery?: string
  // When the modal is controlled externally (search handoff), the
  // built-in trigger button can be suppressed.
  hideTrigger?: boolean
}) {
  const [internalOpen, setInternalOpen] = useState(false)
  const open = controlledOpen ?? internalOpen
  const setOpen = useCallback(
    (v: boolean) => {
      if (onOpenChange) onOpenChange(v)
      else setInternalOpen(v)
    },
    [onOpenChange],
  )

  const [question, setQuestion] = useState('')
  const [state, setState] = useState<AskState>({ status: 'idle' })
  const inputRef = useRef<HTMLInputElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    if (initialQuery !== undefined) setQuestion(initialQuery)
    requestAnimationFrame(() => inputRef.current?.focus())
    // Only re-run when the modal opens or the handed-over query changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialQuery])

  const close = useCallback(() => {
    setOpen(false)
    setState({ status: 'idle' })
    setQuestion('')
  }, [setOpen])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, close])

  // Lock scroll while open.
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  useFocusTrap(overlayRef, open)

  async function ask(e: React.FormEvent) {
    e.preventDefault()
    const q = question.trim()
    if (!q) return
    setState({ status: 'loading' })
    try {
      const res = await fetch('/api/help/ask', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ question: q }),
      })
      const data = await res.json()
      if (!data.ok) {
        setState({ status: 'error', message: data.error || 'Something went wrong.' })
        return
      }
      setState({
        status: 'answered',
        answer: data.answer ?? null,
        message: data.message,
        sources: Array.isArray(data.sources) ? data.sources : [],
        enabled: Boolean(data.enabled),
      })
    } catch {
      setState({ status: 'error', message: 'Network error. Please try again.' })
    }
  }

  return (
    <>
      {!hideTrigger && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={cn(
            'group inline-flex items-center gap-2 rounded-md border bg-card text-left text-sm text-foreground transition hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
            variant === 'button' ? 'px-3 py-2' : 'w-full px-3 py-2',
          )}
          aria-haspopup="dialog"
        >
          <Sparkles className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <span className="flex-1">Ask AI</span>
        </button>
      )}

      {open && (
        <div
          ref={overlayRef}
          role="dialog"
          aria-modal="true"
          aria-label="Ask AI about the docs"
          className="fixed inset-0 z-[100] flex items-start justify-center bg-foreground/30 px-4 pt-[10vh] backdrop-blur-sm"
          onMouseDown={(e) => {
            if (e.target === overlayRef.current) close()
          }}
        >
          <div className="flex max-h-[80vh] w-full max-w-xl flex-col overflow-hidden rounded-xl border bg-card shadow-2xl">
            <form onSubmit={ask} className="flex items-center gap-3 border-b px-4">
              <Sparkles className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
              <input
                ref={inputRef}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                type="text"
                placeholder="Ask a question about Idukki…"
                className="w-full bg-transparent py-4 text-sm outline-none placeholder:text-muted-foreground"
              />
              <button
                type="submit"
                aria-label="Ask"
                className="rounded-md p-1 text-muted-foreground transition hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
              >
                <CornerDownLeft className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                className="rounded-md p-1 text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </form>

            <div className="min-h-0 flex-1 overflow-y-auto p-5">
              {state.status === 'idle' && (
                <p className="text-sm text-muted-foreground">
                  Ask in plain English. Answers are grounded in the Idukki help guides and link to their sources.
                </p>
              )}

              {state.status === 'loading' && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Searching the guides…
                </div>
              )}

              {state.status === 'error' && (
                <p className="text-sm text-destructive">{state.message}</p>
              )}

              {state.status === 'answered' && (
                <div className="space-y-5">
                  {state.message && !state.answer && (
                    <p className="rounded-lg border border-border/60 bg-secondary/30 px-3 py-2 text-sm text-muted-foreground">
                      {state.message}
                    </p>
                  )}
                  {state.answer && (
                    <div className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">{state.answer}</div>
                  )}

                  {state.sources.length > 0 && (
                    <div>
                      <div className="mb-2 font-display text-xs font-medium uppercase tracking-widest text-muted-foreground">
                        {state.answer ? 'Sources' : 'Matching guides'}
                      </div>
                      <ul className="space-y-1.5">
                        {state.sources.map((s) => (
                          <li key={s.slug}>
                            <Link
                              href={s.url}
                              onClick={close}
                              className="group flex items-start gap-2 rounded-md px-2 py-1.5 transition hover:bg-secondary/50"
                            >
                              <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary" aria-hidden="true" />
                              <span className="min-w-0">
                                <span className="block text-sm font-medium text-foreground group-hover:text-primary">{s.title}</span>
                                <span className="block text-xs text-muted-foreground">{s.category}</span>
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {!state.answer && state.sources.length === 0 && (
                    <p className="text-sm text-muted-foreground">No matching guides. Try rephrasing, or email support@idukki.io.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
